/**
 * CodeMirror hover tooltip backed by intelligence/hover.
 *
 * Renders Engine-computed hover info with a lightweight Markdown→HTML
 * converter tuned for the compact format produced by the hover handler.
 */

import type { EditorView } from "@codemirror/view";
import { hoverTooltip, type Tooltip } from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";
import { useEditorStore } from "../../store/editor-store";
import {
    buildUnitDependencyTreesFromSource,
    canRunUnitDependencyNode,
    findHoverUnitIdInSource,
    findRenderTargetIdInSource,
    getPredecessorRowsForTarget,
    getSuccessorRowsForTarget,
    type UnitDependencyDisplayRow,
    type UnitDependencyKind,
    type UnitDependencyTreeNode,
} from "../unit-dependency-tree";
import { createIntelligenceSessionResolver } from "./session";
import type { IntelligenceConfig } from "./types";

let hasLoggedHoverFailure = false;

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── Lightweight Markdown → HTML ── */

/**
 * Convert the hover handler's Markdown subset to HTML.
 * Handles: `---` separator, bold, inline code, code blocks,
 * unordered lists, plain paragraphs, and custom markers:
 *   {{syntax:X}}       → syntax badge indicator
 *   {{icon:Name}}      → plugin/icon indicator
 *   {{kind:text}}      → compact kind chip
 *   {{lines:N-M}}      → line range badge
 *   {{comment}}...{{/comment}} → attached source comment block
 *   {{details:title}}   → collapsible section open
 *   {{/details}}        → collapsible section close
 */
export function mdToHtml(md: string): string {
    const lines = md.split("\n");
    const out: string[] = [];
    let i = 0;
    let isFirstLine = true;

    while (i < lines.length) {
        const line = lines[i];

        // Collapsible section: {{details:title}} ... {{/details}}
        if (line.trim().startsWith("{{details:")) {
            const summaryRaw = line.trim().slice("{{details:".length, -2); // strip }}
            const summaryHtml = inline(summaryRaw);
            out.push(
                `<details class="mh-details"><summary class="mh-summary">${summaryHtml}</summary>`,
            );
            i++;
            // Process inner content until {{/details}} (guard against unclosed blocks)
            const detailsStart = i;
            while (
                i < lines.length &&
                lines[i].trim() !== "{{/details}}" &&
                i - detailsStart < 500
            ) {
                const inner = lines[i];
                // Inner code block
                if (inner.startsWith("```")) {
                    const lang = inner.slice(3).trim();
                    i++;
                    const codeLines: string[] = [];
                    while (i < lines.length && !lines[i].startsWith("```")) {
                        codeLines.push(esc(lines[i]));
                        i++;
                    }
                    if (i < lines.length) i++; // skip closing ```
                    const cls = lang === "json" ? ' class="mh-lang-json"' : "";
                    out.push(
                        `<pre class="mh-code"><code${cls}>${codeLines.join("\n")}</code></pre>`,
                    );
                    continue;
                }
                // Inner regular line
                if (inner.trim() !== "") {
                    out.push(`<div class="mh-line">${inline(inner)}</div>`);
                }
                i++;
            }
            if (i < lines.length) i++; // skip {{/details}}
            out.push("</details>");
            continue;
        }

        // Attached source comment block. This intentionally renders without a
        // visible label; the visual treatment carries the note semantics.
        if (line.trim() === "{{comment}}") {
            i++;
            const noteLines: string[] = [];
            while (i < lines.length && lines[i].trim() !== "{{/comment}}") {
                noteLines.push(lines[i]);
                i++;
            }
            if (i < lines.length) i++;
            out.push(
                `<div class="mh-comment">${noteLines
                    .map((noteLine) =>
                        noteLine.trim() === ""
                            ? '<div class="mh-comment-line mh-comment-empty">&nbsp;</div>'
                            : `<div class="mh-comment-line">${inline(noteLine)}</div>`,
                    )
                    .join("")}</div>`,
            );
            continue;
        }

        // Horizontal rule → thin separator
        if (line.trim() === "---") {
            out.push('<hr class="mh-sep"/>');
            i++;
            continue;
        }

        // Code block: ```lang ... ```
        if (line.startsWith("```")) {
            const lang = line.slice(3).trim();
            i++;
            const codeLines: string[] = [];
            while (i < lines.length && !lines[i].startsWith("```")) {
                codeLines.push(esc(lines[i]));
                i++;
            }
            if (i < lines.length) i++; // skip closing ```
            const cls = lang === "json" ? ' class="mh-lang-json"' : "";
            out.push(
                `<pre class="mh-code"><code${cls}>${codeLines.join("\n")}</code></pre>`,
            );
            continue;
        }

        // Unordered list: - item
        if (/^- /.test(line)) {
            const items: string[] = [];
            while (i < lines.length && /^- /.test(lines[i])) {
                items.push(inline(lines[i].slice(2)));
                i++;
            }
            out.push(
                `<ul class="mh-list">${items.map((it) => `<li>${it}</li>`).join("")}</ul>`,
            );
            continue;
        }

        // Empty line → skip
        if (line.trim() === "") {
            i++;
            continue;
        }

        // First non-empty line → title row
        if (isFirstLine) {
            out.push(`<div class="mh-title">${inline(line)}</div>`);
            isFirstLine = false;
            i++;
            continue;
        }

        // Regular line
        out.push(`<div class="mh-line">${inline(line)}</div>`);
        i++;
    }
    return out.join("");
}

function esc(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inline(text: string): string {
    let s = esc(text);
    s = s.replace(/\{\{syntax:([^}]*)\}\}/g, (_m, kind: string) => {
        const letter = kind ? kind.slice(0, 1) : "?";
        const safeKind = esc(kind || "unknown");
        return `<span class="mh-syntax" data-kind="${safeKind}"><span class="mh-syntax-main">${esc(letter)}</span></span>`;
    });
    // Custom markers: {{icon:Name}}, {{lines:N-M}}, {{lineinfo:N-M}}
    s = s.replace(/\{\{icon:([^}]*)\}\}/g, (_m, name: string) => {
        const letter = name ? name.charAt(0).toUpperCase() : "?";
        return `<span class="mh-icon" title="${name}">${letter}</span>`;
    });
    s = s.replace(/\{\{kind:([^}]*)\}\}/g, (_m, label: string) => {
        return `<span class="mh-kind">${esc(label)}</span>`;
    });
    s = s.replace(/\{\{lines:(\d+-\d+)\}\}/g, (_m, range: string) => {
        return `<span class="mh-badge">L${range}</span>`;
    });
    s = s.replace(
        /\{\{lineinfo:(\d+)-(\d+)\}\}/g,
        (_m, start: string, end: string) => {
            return `<span class="mh-lineinfo">L${start}-${end}</span>`;
        },
    );
    // Bold: **text**
    s = s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
    // Italic: *text* (not inside **)
    s = s.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, "<i>$1</i>");
    // Inline code: `text`
    s = s.replace(/`([^`]+?)`/g, "<code>$1</code>");
    // Arrow: →
    s = s.replace(/→/g, '<span class="mh-arrow">→</span>');
    // Middle dot separator: ·
    s = s.replace(/ · /g, ' <span class="mh-dot">·</span> ');
    return s;
}

/* ── Interactive dependency tree ── */

function kindLabel(kind: UnitDependencyKind): string {
    switch (kind) {
        case "definition":
            return "define";
        case "for":
            return "for";
        case "content":
            return "content";
        case "import":
            return "import";
        case "use":
            return "use";
    }
}

function dispatchRunEvent(node: UnitDependencyTreeNode) {
    const id = node.unit.id;
    switch (node.unit.kind) {
        case "definition":
            window.dispatchEvent(new CustomEvent("magic-md:run-definition", {
                detail: { definitionId: id },
            }));
            return;
        case "for":
            window.dispatchEvent(new CustomEvent("magic-md:run-for", {
                detail: { forId: id },
            }));
            return;
        case "content":
            window.dispatchEvent(new CustomEvent("magic-md:run-content", {
                detail: { definitionId: id },
            }));
            return;
        case "import":
            window.dispatchEvent(new CustomEvent("magic-md:run-import", {
                detail: { namespace: id },
            }));
            return;
        case "use":
            window.dispatchEvent(new CustomEvent("magic-md:run-use", {
                detail: { useId: id },
            }));
            return;
    }
}

function dispatchGotoLine(line: number) {
    window.dispatchEvent(new CustomEvent("magic-md:goto-line", {
        detail: { line, flash: true },
    }));
}

function createDependencySection(
    titleText: string,
    rows: UnitDependencyDisplayRow[],
): HTMLElement {
    const section = document.createElement("div");
    section.className = "mh-deps";

    const title = document.createElement("div");
    title.className = "mh-deps-title";
    title.textContent = titleText;
    section.appendChild(title);

    const list = document.createElement("div");
    list.className = "mh-deps-list";

    for (const { node, depth } of rows) {
        const row = document.createElement("div");
        row.className = "mh-deps-row";
        row.style.paddingLeft = `${6 + depth * 14}px`;

        const jump = document.createElement("button");
        jump.type = "button";
        jump.className = "mh-deps-jump";
        jump.title = tr("intelligence.jumpTo", { id: node.unit.id });
        jump.addEventListener("mousedown", (event) => event.preventDefault());
        jump.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            dispatchGotoLine(node.unit.line);
        });

        const branch = document.createElement("span");
        branch.className = "mh-deps-branch";
        branch.setAttribute("aria-hidden", "true");
        jump.appendChild(branch);

        const label = document.createElement("span");
        label.className = "mh-deps-label";
        label.textContent = node.unit.id;
        jump.appendChild(label);

        const kind = document.createElement("span");
        kind.className = "mh-deps-kind";
        kind.textContent = kindLabel(node.unit.kind);
        jump.appendChild(kind);

        if (node.repeated || node.cycle) {
            const marker = document.createElement("span");
            marker.className = "mh-deps-marker";
            marker.textContent = node.cycle ? "cycle" : "seen";
            jump.appendChild(marker);
        }

        row.appendChild(jump);
        if (canRunUnitDependencyNode(node)) {
            const run = document.createElement("button");
            run.type = "button";
            run.className = "mh-deps-run";
            run.title = tr("intelligence.run", { id: node.unit.id });
            run.setAttribute("aria-label", run.title);
            run.textContent = "▶";
            run.addEventListener("mousedown", (event) => event.preventDefault());
            run.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                dispatchRunEvent(node);
            });
            row.appendChild(run);
        }
        list.appendChild(row);
    }

    section.appendChild(list);
    return section;
}

function attachTitleRunButton(
    dom: HTMLElement,
    node: UnitDependencyTreeNode,
    label = node.unit.id,
) {
    const title = dom.querySelector<HTMLElement>(".mh-title");
    if (!title) return;
    if (!canRunUnitDependencyNode(node)) return;

    title.classList.add("mh-title-has-run");
    const run = document.createElement("button");
    run.type = "button";
    run.className = "mh-title-run";
    run.title = tr("intelligence.run", { id: label });
    run.setAttribute("aria-label", run.title);
    run.textContent = "▶";
    run.addEventListener("mousedown", (event) => event.preventDefault());
    run.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        dispatchRunEvent(node);
    });

    title.appendChild(run);
}

/* ── Factory ── */

export function intelligenceHover(config: IntelligenceConfig) {
    const resolveSession = createIntelligenceSessionResolver(config);

    return hoverTooltip(
        async (view: EditorView, pos: number): Promise<Tooltip | null> => {
            if (!useEditorStore.getState().syntaxHoverDetailEnabled) {
                return null;
            }

            // Suppress tooltip when hovering over action buttons — they have
            // their own title tooltips and the hover card would obstruct them.
            if (view.dom.querySelector(".cm-magic-btn-group:hover")) return null;

            const line = view.state.doc.lineAt(pos);
            const lineNumber = line.number;
            const column = pos - line.from;
            const source = view.state.doc.toString();
            const dependencyTargetId = findHoverUnitIdInSource(
                source,
                lineNumber,
                column,
            );
            const renderTargetId = findRenderTargetIdInSource(
                source,
                lineNumber,
                column,
            );
            const dependencyTrees = dependencyTargetId
                ? buildUnitDependencyTreesFromSource(source, dependencyTargetId)
                : { predecessor: null, successor: null };
            const predecessorTree = dependencyTrees.predecessor;
            const successorTree = dependencyTrees.successor;

            let contents: string | null = null;
            const engine = config.getEngine();
            if (engine?.intelligenceHover) {
                try {
                    const session = await resolveSession();
                    const hover = session?.engine.intelligenceHover;
                    if (session && hover) {
                        const result = await hover({
                            sessionId: session.sessionId,
                            line: lineNumber,
                            column,
                            locale: config.getLocale?.(),
                        });
                        contents = result?.contents ?? null;
                    }
                } catch (err) {
                    if (!hasLoggedHoverFailure) {
                        hasLoggedHoverFailure = true;
                        console.warn("[magic-md] intelligenceHover RPC failed:", err);
                    }
                }
            }

            if (!contents) {
                return null;
            }

            return {
                pos,
                above: false,
                create() {
                    const dom = document.createElement("div");
                    dom.className = "cm-intelligence-hover";
                    if (contents) {
                        dom.innerHTML = mdToHtml(contents);
                    }
                    const titleRunTree = predecessorTree ?? successorTree;
                    if (renderTargetId && titleRunTree) {
                        attachTitleRunButton(dom, titleRunTree, renderTargetId);
                    }
                    if (dependencyTargetId && predecessorTree) {
                        const rows = getPredecessorRowsForTarget(
                            dependencyTargetId,
                            predecessorTree,
                        );
                        if (rows.length > 0) {
                            dom.appendChild(
                                createDependencySection(
                                    tr("intelligence.predecessors", { id: dependencyTargetId }),
                                    rows,
                                ),
                            );
                        }
                    }
                    if (dependencyTargetId && successorTree) {
                        const rows = getSuccessorRowsForTarget(
                            dependencyTargetId,
                            successorTree,
                        );
                        if (rows.length > 0) {
                            dom.appendChild(
                                createDependencySection(
                                    tr("intelligence.successors", { id: dependencyTargetId }),
                                    rows,
                                ),
                            );
                        }
                    }
                    return { dom };
                },
            };
        },
        { hoverTime: 300 },
    );
}
